import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import {
  organizationTraysItemsSelector,
  organizationTraysRemoteTotalSelector,
  isLoadingOrganizationTraysSelector,
  getOrganizationTrayInventoriesListAction,
  cleanOrganizationTraysAction,
} from 'state/slices/organizations';
import { usePagination } from 'hooks/pagination';
import {
  Organization,
  PaginationData,
  Tray,
  TrayInventorySummary,
} from 'constants/interface';
import { Search, Table } from 'UI';

import {
  SearchContainer,
} from './style';

interface InventoryViewProps {
  shouldReload: boolean
  onReloadFinish(): void
  activeOrganization: Organization
}

const InventoryView = ({
  shouldReload,
  onReloadFinish,
  activeOrganization,
}: InventoryViewProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(organizationTraysRemoteTotalSelector);

  const inventoryItems = useSelector(organizationTraysItemsSelector);
  const isRemoteLoading = useSelector(isLoadingOrganizationTraysSelector);

  const inventories = inventoryItems.map((inventory) => {
    const tableInventory: TrayInventorySummary & {
      key?: string,
    } = {
      ...inventory,
      key: `${inventory.tray.id.toString()}/${inventory.owner.id.toString()}`,
    };
    return tableInventory;
  }).filter((tableInventory): tableInventory is TrayInventorySummary => !!tableInventory);

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    dispatch(getOrganizationTrayInventoriesListAction({
      ...paginationData,
      organization: activeOrganization.id,
    }));
  };
  const pagination = usePagination<TrayInventorySummary>(
    remoteFetch,
    {
      filter: {
        tray: undefined,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanOrganizationTraysAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  useEffect(() => {
    if (shouldReload) {
      pagination.fetch();
      onReloadFinish();
    }
  }, [shouldReload]);

  const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  return (
    <>
      <SearchContainer>
        <Search placeholder="Buscar no estoque" onSearch={pagination.onSearch} allowClear />
      </SearchContainer>
      <Table
        dataSource={inventories}
        pagination={pagination.getInternal()}
        loading={isRemoteLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="Bandeja"
          key="tray"
          dataIndex="tray"
          render={(tray: Omit<
            Tray,
            'createdAt' |
            'updatedAt' |
            'createdBy' |
            'updatedBy'
            >) => `Bandeja ${tray.capacity}${tray.isHydroponic ? ' Hidropônica' : ''}`}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'tray' ? pageData.order : undefined}
        />
        <Table.Column
          title="Saldo atual"
          key="quantity"
          dataIndex="quantity"
          render={(quantity: number) => {
            const formattedQuantity = Intl
              .NumberFormat(
                'pt-BR',
              ).format(quantity);
            const content = `${formattedQuantity} bandeja${quantity > 1 ? 's' : ''}`;
            return content;
          }}
        />
        <Table.Column
          title="Custo médio"
          key="averageCost"
          dataIndex="averageCost"
          render={(averageCost: number) => {
            const currencyValue = Intl
              .NumberFormat(
                'pt-BR',
                {
                  style: 'currency',
                  currency: 'BRL',
                },
              ).format((averageCost / 100));
            return currencyValue;
          }}
        />
      </Table>
    </>
  );
};

export default InventoryView;
